import { Box } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  BearerOffsets,
  FIRST_BEARER_PAGES,
  useCapabilityPart,
} from "../../hooks/queries/useCapabilityPart";
import { BearerGroup } from "../../types/capabilityPartDetail";
import type { EntityDetailProps } from "../../types/entityRegistry";
import type { MainEntity } from "../../types/entityTypes";
import DetailSections from "../Detail/DetailSections";
import {
  EntityNavigationProvider,
  entityLinkText,
} from "../Detail/EntityLink";
import { SourceJumpProvider } from "../Detail/sourceJump";
import EntityFunctionalHeader from "../Layout/EntityDetailOverlay/EntityFunctionalHeader";
import { EntityDetailStatus } from "../Layout/EntityDetailStatus";
import PartHead from "./PartHead";
import { partSections } from "./partSections";

// Width (px) the view keeps to on a page of its own
const STANDALONE_WIDTH = 1200;

/**
 * The detail of a capability part: the registry's Detail component of the
 * type. The same view serves every role: an ability, a function area, an
 * effect area, a condition, a mode, an object and a means.
 *
 * It fetches the part by its id and shows the overlay's header, the head,
 * and the sections under a strip that stays in view. Loading, not found and
 * graph unavailable show where the existing details show them.
 */
const CapabilityPartDetailView: React.FC<EntityDetailProps> = ({
  entityId,
  mode = "overlay",
  onEntityClick,
  onClose,
  scrollContainerRef,
}) => {
  // Where each bearer tab's page starts, for the part they were turned on:
  // another part starts with every tab at its first page
  const [paging, setPaging] = useState<{
    partId: string;
    offsets: BearerOffsets;
  }>({ partId: entityId, offsets: FIRST_BEARER_PAGES });
  const offsets =
    paging.partId === entityId ? paging.offsets : FIRST_BEARER_PAGES;

  const { part, loading, turning, error } = useCapabilityPart(
    entityId || null,
    offsets,
  );

  // One tab is turned by asking for its offset; the others keep theirs
  const handleBearerPage = useCallback(
    (group: BearerGroup, offset: number) =>
      setPaging((previous) => ({
        partId: entityId,
        offsets: {
          ...(previous.partId === entityId
            ? previous.offsets
            : FIRST_BEARER_PAGES),
          [group]: offset,
        },
      })),
    [entityId],
  );

  const ownScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? ownScrollContainerRef;

  const sections = useMemo(
    () =>
      part
        ? partSections(part, {
            mode,
            turning,
            onBearerPage: handleBearerPage,
          })
        : [],
    [part, mode, turning, handleBearerPage],
  );

  // What the header reads: it shows the type and opens the full page
  const mainEntity = useMemo<MainEntity | null>(
    () =>
      part
        ? {
            id: part.id,
            name: entityLinkText(part.type, part.label),
            entityType: "capability_part",
          }
        : null,
    [part],
  );

  // Another part starts at its top
  const partId = part?.id;
  useEffect(() => {
    if (partId && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [partId, scrollRef]);

  if (loading || error) {
    return <EntityDetailStatus loading={loading} error={error} mode={mode} />;
  }

  if (!part || !mainEntity) {
    return null;
  }

  return (
    <EntityNavigationProvider onEntityClick={onEntityClick}>
      <SourceJumpProvider>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EntityFunctionalHeader
            mainEntity={mainEntity}
            onClose={onClose || (() => {})}
            mode={mode}
          />
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Box
              sx={{
                maxWidth: mode === "standalone" ? STANDALONE_WIDTH : "100%",
                mx: "auto",
              }}
            >
              <PartHead part={part} />
              <DetailSections
                sections={sections}
                scrollContainerRef={scrollRef}
              />
            </Box>
          </Box>
        </Box>
      </SourceJumpProvider>
    </EntityNavigationProvider>
  );
};

export default CapabilityPartDetailView;
